import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationCenter = ({ userRole = 'contractor' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);

  const mockNotifications = {
    superadmin: [
      {
        id: 1,
        type: 'system',
        title: 'Nieuwe affiliate aanmelding',
        message: 'TechPartners BV heeft zich aangemeld als affiliate',
        timestamp: '2026-01-14T20:15:00',
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'financial',
        title: 'Commissie uitbetaling verwerkt',
        message: '€2.450,00 uitbetaald aan 12 affiliates',
        timestamp: '2026-01-14T18:30:00',
        read: false,
        priority: 'medium'
      },
      {
        id: 3,
        type: 'alert',
        title: 'Systeem onderhoud gepland',
        message: 'Gepland voor 15 januari 2026, 02:00-04:00',
        timestamp: '2026-01-14T16:00:00',
        read: true,
        priority: 'low'
      }
    ],
    client: [
      {
        id: 1,
        type: 'job',
        title: 'Nieuwe aanmelding ontvangen',
        message: 'Jan de Vries heeft zich aangemeld voor "Airco installatie"',
        timestamp: '2026-01-14T19:45:00',
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'job',
        title: 'Opdracht voltooid',
        message: 'Pieter Jansen heeft "Zonnepanelen installatie" afgerond',
        timestamp: '2026-01-14T17:20:00',
        read: false,
        priority: 'medium'
      }
    ],
    contractor: [
      {
        id: 1,
        type: 'job',
        title: 'Nieuwe opdracht beschikbaar',
        message: 'Warmtepomp installatie in Amsterdam - €1.200',
        timestamp: '2026-01-14T20:30:00',
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'payment',
        title: 'Betaling ontvangen',
        message: '€850,00 voor "Airco installatie Rotterdam"',
        timestamp: '2026-01-14T18:15:00',
        read: false,
        priority: 'medium'
      },
      {
        id: 3,
        type: 'review',
        title: 'Nieuwe beoordeling',
        message: 'Klant heeft 5 sterren gegeven voor je laatste opdracht',
        timestamp: '2026-01-14T15:45:00',
        read: true,
        priority: 'low'
      }
    ],
    affiliate: [
      {
        id: 1,
        type: 'commission',
        title: 'Nieuwe commissie verdiend',
        message: '€45,00 commissie voor verwezen opdracht',
        timestamp: '2026-01-14T19:00:00',
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'referral',
        title: 'Nieuwe verwijzing geregistreerd',
        message: 'TechBouw BV heeft zich aangemeld via jouw link',
        timestamp: '2026-01-14T16:30:00',
        read: false,
        priority: 'medium'
      }
    ]
  };

  useEffect(() => {
    setNotifications(mockNotifications?.[userRole] || mockNotifications?.contractor);
  }, [userRole]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const markAsRead = (id) => {
    setNotifications(notifications?.map(n => 
      n?.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications?.map(n => ({ ...n, read: true })));
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      system: 'Settings',
      financial: 'DollarSign',
      alert: 'AlertCircle',
      job: 'Briefcase',
      payment: 'CreditCard',
      review: 'Star',
      commission: 'TrendingUp',
      referral: 'Users'
    };
    return iconMap?.[type] || 'Bell';
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      high: 'text-error',
      medium: 'text-warning',
      low: 'text-muted-foreground'
    };
    return colorMap?.[priority] || 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) {
      return `${diffMins} min geleden`;
    } else if (diffHours < 24) {
      return `${diffHours} uur geleden`;
    } else {
      return date?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleDropdown}
        className="relative"
        aria-label="Meldingen"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs font-medium rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-popover border border-border rounded-lg elevation-lg z-1010 overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold text-base text-foreground">
              Meldingen
            </h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                Alles markeren als gelezen
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications?.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Icon name="Bell" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Geen meldingen</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    onClick={() => markAsRead(notification?.id)}
                    className={`
                      px-4 py-3 cursor-pointer transition-smooth hover:bg-muted
                      ${!notification?.read ? 'bg-primary/5' : ''}
                    `}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 ${getPriorityColor(notification?.priority)}`}>
                        <Icon name={getNotificationIcon(notification?.type)} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-medium text-sm text-foreground truncate">
                            {notification?.title}
                          </h4>
                          {!notification?.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-1 line-clamp-2">
                          {notification?.message}
                        </p>
                        <span className="caption text-muted-foreground">
                          {formatTimestamp(notification?.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-border">
            <Button variant="ghost" size="sm" fullWidth>
              Alle meldingen bekijken
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;